import React from 'react';
import { CyNumber } from "../../utilities/number";
import MonteCarloConversion from "../../utilities/CyMonteCarloConversion.js";
import { ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label, Area, Line } from 'recharts';
import Typography from "@material-ui/core/Typography";
import MonteCarloDisclosure from "../../containers/SummaryPDF/components/MonteCarloDisclosure";


/*
This function generates the custom tooltip displayed on the bar chart when a bar is hovered over.
******************************************************************************************************
inputs: 
active - boolean to show oir hide the tool tip calculated by Recharts
Payload - the source data to be displayed by the tooltip
******************************************************************************************************
outouts:
null
*/
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {

    let percentileToolTip = [];
    percentileToolTip.push(payload[0].value)

    for (let i = 1; i < payload.length-1; i++){
      percentileToolTip.push((parseFloat(payload[i].value) + parseFloat(percentileToolTip[i-1])).toFixed(2))
    }
   
    return (
      <div className="custom-tooltip">
        <p className="label" fontSize={12}>{`Base Investment : ${CyNumber.CurrencyFormatter(percentileToolTip[0])}`}</p>
        <p className="label" fontSize={12}>{`10th Percentile : ${CyNumber.CurrencyFormatter(percentileToolTip[1])}`}</p>
        <p className="label" fontSize={12}>{`25th Percentile : ${CyNumber.CurrencyFormatter(percentileToolTip[2])}`}</p>
        <p className="label" fontSize={12}>{`50th Percentile : ${CyNumber.CurrencyFormatter(percentileToolTip[3])}`}</p>
        <p className="label" fontSize={12}>{`75th Percentile : ${CyNumber.CurrencyFormatter(percentileToolTip[4])}`}</p>
        <p className="label" fontSize={12}>{`90th Percentile : ${CyNumber.CurrencyFormatter(percentileToolTip[5])}`}</p>

      </div>
    );
  }

  return null;
};
const renderLegend = (props) => {
  const { payload } = props;
  const legendData = [
    payload[6], payload[3], payload[5]
  ];
  return <ul className="recharts-default-legend">
    {
      legendData.map(_ => (
        <li key={_.value} style={{display: "inline-block", marginRight: 10}}>
          <svg width="14" height="14" viewBox="0 0 32 32"
               style={{display: "inline-block", verticalAlign: "middle", marginRight: 4}}>
            <path stroke="none" fill={_.color} d="M0,4h32v24h-32z"/>
          </svg>
          <span>{_.value}</span>
        </li>
      ))
    }
  </ul>
}

const CyMonteCarlo = (props) => {
  const mean = props.mean; //mean of the dataset 
  const sd = props.sd; //standard deviation of the dataset
  const initialInvestment = props.initialInvestment; //initial investment
  const forPrinting = props.forPrinting || false;
  const years = 20; //number of years to simulate
  const iterations = 10000;
  const dataP = MonteCarloConversion(mean, sd, initialInvestment, years, iterations); //array that holds the data to be used when generating the graph
  const median = dataP.pop(); //removes the median from the array
  const baseInvestment = dataP.pop();
  const { CurrencyFormatter } = CyNumber;
  const colors = ["#FFFFFF", "#9edcbd", "#74cfa4", "#51c490", "#28ba7c", "#21aa71"];
  /*
  This for loop is used to process the data so that the graph is properly displayed in react.
  When displaying a stacked bar chart in recharts the bars are stacked at their full value, this
  resulted in graphs that where massively larger than they should be. This function reduces the
  different bars to be stacked to the difference from the previous entry.
  */
  for (let i = dataP.length-1; i > 0; i--){
    for (let j = 0; j < dataP[0].length; j++){
      dataP[i][j] -= dataP[i-1][j];
      dataP[i][j] = parseFloat(dataP[i][j]).toFixed(2);
      if(i === 1){
        dataP[i-1][j] -= baseInvestment;
        dataP[i-1][j] = parseFloat(dataP[i-1][j]).toFixed(2);
      }
    }
  }
  const data = [];
  //for loop to organize the data for being graphed
  for (let i = 0; i < years; i++){
    data.push({
      name: (i+1),
      "investment": baseInvestment,
      "10th Percentile": dataP[0][i],
      "25th Percentile": dataP[1][i],
      "25th - 75th Percentile": dataP[2][i],
      "75th Percentile": dataP[3][i],
      "10th - 90th Percentile": dataP[4][i],
      "Median": median[i],
    });

  }
  return (
    <div>
      {
        forPrinting === false &&
        <Typography variant={"h5"} color={"primary"} align={"center"}>
          Monte Carlo Projection
        </Typography>
      }

      <Typography
        variant={forPrinting ? "h6" : "body1"}
        align={forPrinting ? "left" : "center"}
      >
        20 Year Projected Growth
      </Typography>
      <ResponsiveContainer width="100%" height={500}>
        <ComposedChart
          data={data}
          margin={{
            top: 5,
            right: 40,
            left: 50,
            bottom: 30,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="name" fontSize={12}>
            <Label value="Time (Years)" offset={0} position="insideBottom" align='center' fontSize={12} />
          </ XAxis>
          <YAxis tickFormatter={CurrencyFormatter} fontSize={12}/>
          <Tooltip content={<CustomTooltip data={baseInvestment}/>} wrapperStyle={{ backgroundColor: "white", borderStyle: "ridge", paddingLeft: "10px", paddingRight: "10px", fontSize:14}}/>

          <Legend
            wrapperStyle={{
              fontSize: "14px",
              position: 'relative',
              left: 0,
              width: '100%',
              bottom: '40px',
              textAlign: 'center'
            }}
            content={renderLegend}
          />

          <Area isAnimationActive={!forPrinting} dataKey="investment" stackId="a" fill={colors[0]} fillOpacity={0} legendType='none' stroke='none' />
          <Area isAnimationActive={!forPrinting} dataKey="10th Percentile" stackId="a" fill={colors[1]} fillOpacity={0} stroke='none' legendType='none'/>
          <Area isAnimationActive={!forPrinting} dataKey="25th Percentile" stackId="a" fill={colors[2]} stroke={colors[2]} legendType='none' strokeOpacity={0.75} fillOpacity={0.75}/>
          <Area isAnimationActive={!forPrinting} dataKey="25th - 75th Percentile" stackId="a" fill={colors[4]} stroke={colors[4]} legendType='rect'strokeOpacity={0.75} fillOpacity={0.75}/>
          <Area isAnimationActive={!forPrinting} dataKey="75th Percentile" stackId="a" fill={colors[4]} stroke={colors[4]} legendType='none'strokeOpacity={0.75} fillOpacity={0.75}/>
          <Area isAnimationActive={!forPrinting} dataKey="10th - 90th Percentile" stackId="a" fill={colors[2]} stroke={colors[2]} legendType='rect'strokeOpacity={0.75} fillOpacity={0.75}/>
          <Line isAnimationActive={!forPrinting} dataKey="Median" stroke="#333333" strokeWidth={2} legendType='rect' dot={false} strokeDasharray='3 3'/>

        </ComposedChart>
      </ResponsiveContainer>

      <MonteCarloDisclosure
        standardDeviation={sd}
        annualReturn={mean}
        startDate={props.startDate}
      />
    </div>
  );
}
export default CyMonteCarlo;


// WEBPACK FOOTER //
// ./src/components/Charts/CyMonteCarlo.jsx